/**
 * PushSubscribeCard — Tarjeta persistente para gestionar suscripción Push.
 * Diseñada para que Ronald (admin) pueda suscribirse desde su iPhone.
 * Incluye instrucciones específicas para iOS.
 */
import { Bell, BellSlash, ArrowSquareOut, Info, CheckCircle, Warning, Spinner, Rocket } from "@phosphor-icons/react";
import { usePushNotification } from "../lib/usePushNotification";
import { useAuth } from "../contexts/AuthContext";
import { useState } from "react";
import apiClient from "../lib/api";
import { toast } from "sonner";

export default function PushSubscribeCard() {
  const { user } = useAuth();
  const { supported, permission, subscribed, loading, error, isIOS, isStandalone, subscribe, unsubscribe } =
    usePushNotification(user?.id);
  const [testing, setTesting] = useState(false);

  const sendTestPush = async () => {
    setTesting(true);
    try {
      await apiClient.post("/push/test");
      toast.success("Notificación de prueba enviada. Deberías recibirla en segundos.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar prueba");
    }
    setTesting(false);
  };

  const isIOSNotStandalone = isIOS && !isStandalone;

  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(234,179,8,0.2)", background: "rgba(234,179,8,0.03)" }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="w-9 h-9 rounded-xl bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
          <Bell weight="fill" className="w-5 h-5 text-yellow-400" />
        </div>
        <div>
          <div className="text-[10px] text-yellow-400 tracking-[0.25em] uppercase font-bold">Mis Notificaciones</div>
          <div className="text-sm font-semibold text-white">Alertas Push en este dispositivo</div>
        </div>
        {subscribed && (
          <span className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <CheckCircle weight="fill" className="w-3.5 h-3.5" />
            Activas
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-5 pb-5 space-y-4 border-t" style={{ borderColor: "rgba(234,179,8,0.1)" }}>

        {/* iOS not standalone warning */}
        {isIOSNotStandalone && (
          <div className="mt-4 rounded-xl p-4 bg-orange-500/10 border border-orange-500/20 space-y-3">
            <div className="flex items-start gap-2 text-orange-300 text-sm font-semibold">
              <Warning weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>Pasos para activar en iPhone</span>
            </div>
            <ol className="space-y-2 text-xs text-orange-200/80 list-decimal list-inside">
              <li>Abre <strong>Safari</strong> y ve a <strong>ganasconronald.com</strong></li>
              <li>Pulsa el icono <strong>Compartir</strong> (cuadrado con flecha hacia arriba)</li>
              <li>Selecciona <strong>"Agregar a pantalla de inicio"</strong></li>
              <li>Abre la app desde el <strong>icono en tu pantalla de inicio</strong></li>
              <li>Regresa a esta sección y pulsa <strong>"Activar notificaciones"</strong></li>
            </ol>
            <a
              href="/"
              className="inline-flex items-center gap-1.5 text-xs text-orange-300 font-bold hover:text-orange-200 transition-colors"
              onClick={(e) => { e.preventDefault(); window.location.reload(); }}
            >
              <ArrowSquareOut className="w-3.5 h-3.5" />
              Ya lo hice — recargar
            </a>
          </div>
        )}

        {/* Not supported (non-iOS, no PushManager) */}
        {!supported && !isIOS && (
          <div className="mt-4 flex items-start gap-2 text-zinc-400 text-sm">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Las notificaciones push no están soportadas en este navegador. Usa Chrome o Safari 16.4+.</span>
          </div>
        )}

        {/* Permission denied */}
        {supported && permission === "denied" && !isIOSNotStandalone && (
          <div className="mt-4 flex items-start gap-2 text-red-400/80 text-sm">
            <BellSlash weight="fill" className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Las notificaciones están bloqueadas en tu navegador. Ve a Configuración → Safari/Chrome → Notificaciones y permite las notificaciones para este sitio.
            </span>
          </div>
        )}

        {/* Error message */}
        {error && !error.startsWith("iOS_NOT_STANDALONE") && (
          <div className="mt-4 flex items-start gap-2 text-red-400/80 text-xs bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20">
            <Warning weight="fill" className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Main action button */}
        {(supported || isIOSNotStandalone) && permission !== "denied" && (
          <div className="mt-4 flex items-center gap-3">
            {subscribed ? (
              <>
                <div className="flex-1 text-xs text-zinc-400">
                  Recibirás alertas cuando se publiquen nuevas jugadas y cuando haya mensajes en el chat.
                </div>
                <button
                  data-testid="push-test-btn"
                  onClick={sendTestPush}
                  disabled={testing}
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20 transition-all text-xs font-semibold disabled:opacity-50"
                >
                  {testing ? <Spinner className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                  Probar
                </button>
                <button
                  data-testid="push-unsubscribe-btn"
                  onClick={unsubscribe}
                  disabled={loading}
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500/30 transition-all text-xs font-semibold disabled:opacity-50"
                >
                  {loading ? <Spinner className="w-3.5 h-3.5 animate-spin" /> : <BellSlash className="w-3.5 h-3.5" />}
                  Desactivar
                </button>
              </>
            ) : (
              <>
                <div className="flex-1 text-xs text-zinc-400">
                  {isIOSNotStandalone
                    ? "Sigue los pasos de arriba para activar en iPhone."
                    : "Activa para recibir alertas instantáneas de jugadas y mensajes."}
                </div>
                <button
                  data-testid="push-subscribe-btn"
                  onClick={subscribe}
                  disabled={loading || isIOSNotStandalone}
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/15 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/25 transition-all text-xs font-bold disabled:opacity-40"
                >
                  {loading ? <Spinner className="w-3.5 h-3.5 animate-spin" /> : <Bell weight="fill" className="w-3.5 h-3.5" />}
                  {loading ? "Activando..." : "Activar notificaciones"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
